import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { LucideIcon } from "lucide-react";

import { Spinner } from "src/@/components/ui/spinner";
import { cn } from "src/@/lib/utils";

const buttonVariants = cva(
    "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default:
                    "bg-primary text-primary-foreground shadow hover:bg-primary/90",
                destructive:
                    "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
                "outline-destructive":
                    "border border-destructive bg-background text-destructive shadow-sm hover:bg-accent",
                outline:
                    "border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
                secondary:
                    "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
                ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
            },
            size: {
                default: "h-9 px-4 py-2",
                xs: "h-7 rounded-md px-2 text-xs",
                sm: "h-8 rounded-md px-3 text-xs",
                lg: "h-10 rounded-md px-8",
                icon: "h-9 w-9",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

const iconSizes: Record<ButtonSize, string> = {
    default: "h-4 w-4",
    xs: "h-3 w-3",
    sm: "h-3.5 w-3.5",
    lg: "h-5 w-5",
    icon: "h-4 w-4",
};

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    loading?: boolean;
    icon?: LucideIcon;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size = "default",
            loading,
            children,
            icon: Icon,
            asChild = false,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";
        const iconSize = size
            ? iconSizes[size as ButtonSize]
            : iconSizes.default;

        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            >
                <>
                    {loading ? <Spinner size="sm" /> : null}
                    {Icon && <Icon className={iconSize} />}
                    {children}
                </>
            </Comp>
        );
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
